import * as XLSX from 'xlsx';
import { badgeStatut, badgeRegime } from './etudiantsAffichage';

/**
 * L'EXPORT DE LA LISTE FILTRÉE (lot 1.6 bis, écart E7)
 * ====================================================
 * Spec : doc/modules/etudiants.md §Onglet 1 · maquette : un simple bouton de topbar.
 *
 * ── ⚠️ CE QUI EST À L'ÉCRAN PART, ET RIEN DE PLUS ───────────────────────────
 * Décidé avec Bertrand le 2026-09-11. Le module travaille en LISTE BLANCHE depuis
 * le lot 1.0, et un fichier exporté échappe ensuite à tous les droits de
 * l'application : il circule par courriel, il dort sur une clé. Y glisser les
 * adresses ou les téléphones « parce qu'ils seraient utiles » serait exactement
 * la fuite que la liste blanche empêche partout ailleurs.
 *
 * ⚠️ Aucune colonne n'est ajoutée ici sans décision explicite. Si un jour il en
 * faut une de plus, elle se décide, elle ne se devine pas.
 *
 * ── L'EXPORT SUIT LES FILTRES ACTIFS ────────────────────────────────────────
 * Il n'interroge pas le serveur : il exporte les lignes que l'écran TIENT DÉJÀ.
 * Une seconde lecture aurait pu rendre autre chose que ce qui est affiché — et un
 * export qui ne correspond pas à l'écran est pire qu'une absence d'export.
 *
 * ── DEUX ÉCARTS DE FORME, ET ILS SONT DÉLIBÉRÉS (E53) ───────────────────────
 * 1. Le rang et son dénominateur sont DEUX colonnes numériques, pas « 12/59 ».
 *    Dans un tableur, « 12/59 » est du texte : il ne se trie pas, il ne se somme
 *    pas, et il se transforme parfois tout seul en date. Le tableur existe pour
 *    calculer — lui donner du texte, c'est lui retirer sa raison d'être.
 * 2. Une colonne « Actif » porte ce que la liste dit par un nom BARRÉ. Le barré
 *    ne survit pas à un export : sans cette colonne, un étudiant parti se lirait
 *    comme un inscrit ordinaire.
 */

/** Les colonnes, dans l'ordre. `cle` sert au banc ; `valeur` lit une ligne. */
export const COLONNES_EXPORT = [
    { cle: 'nom', entete: 'Nom', valeur: (l) => l.nom || '' },
    { cle: 'prenom', entete: 'Prénom', valeur: (l) => l.prenom || '' },
    // Le numéro est du TEXTE, jamais un nombre : « 00253975 » perdrait son zéro
    // de tête, et un numéro étudiant ne se calcule pas.
    { cle: 'code_etudiant', entete: 'N° étudiant', valeur: (l) => l.code_etudiant || '' },
    { cle: 'formation', entete: 'Formation', valeur: (l) => l.formation_nom || '' },
    // 2026-10-29 (E.4) : colonne décidée avec Bertrand (« ok pour tout »). Vide = tronc commun.
    { cle: 'parcours', entete: 'Parcours', valeur: (l) => l.parcours_code || '' },
    { cle: 'regime', entete: 'Régime', valeur: (l) => badgeRegime(l.regime_inscription).libelle },
    // `null` n'est PAS `false` : « inconnu » se dit, il ne se devine pas.
    { cle: 'boursier', entete: 'Boursier',
      valeur: (l) => (l.boursier === true ? 'Oui' : l.boursier === false ? 'Non' : '') },
    { cle: 'statut', entete: 'Statut', valeur: (l) => badgeStatut(l.statut).libelle },
    // ⚠️ Voir l'en-tête : le nom barré de la liste ne survit pas à un export.
    { cle: 'actif', entete: 'Actif', valeur: (l) => (l.actif === false ? 'Non' : 'Oui') },
    { cle: 'rang_promotion', entete: 'Rang promo', valeur: (l) => nombreOuVide(l.rang_promotion) },
    { cle: 'rang_promotion_max', entete: 'Promo sur', valeur: (l) => nombreOuVide(l.rang_promotion_max) },
    { cle: 'rang_promotion_semestre', entete: 'Semestre',
      valeur: (l) => nombreOuVide(l.rang_promotion_semestre) },
    { cle: 'classement_recrutement', entete: 'Rang admission',
      valeur: (l) => nombreOuVide(l.classement_recrutement) },
    { cle: 'rang_max_recrutement', entete: 'Admission sur',
      valeur: (l) => nombreOuVide(l.rang_max_recrutement) }
];

/** Un vrai nombre pour le tableur, ou une case VIDE — jamais un `0` inventé, qui
 *  se trierait comme un rang et fausserait toute moyenne. */
function nombreOuVide(v) {
    if (v === null || v === undefined || v === '') return '';
    const n = Number(v);
    return Number.isNaN(n) ? '' : n;
}

/**
 * Le nom du fichier DIT ce qu'il contient : l'export suit les filtres, donc son
 * nom doit les porter. « etudiants.xlsx » téléchargé trois fois avec trois filtres
 * différents donne trois fichiers indiscernables dans un dossier.
 */
export function nomFichierExport({ annee, formation, filtresActifs }) {
    const propre = (t) => String(t || '')
        // ⚠️ La plage des diacritiques s'écrit en ÉCHAPPEMENT : collée telle quelle,
        // elle devient invisible dans le source et le prochain lecteur la casse.
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^A-Za-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    const jour = new Date().toISOString().slice(0, 10);
    const morceaux = ['etudiants'];
    if (annee) morceaux.push(propre(annee));
    if (formation) morceaux.push(propre(formation));
    // « filtre » et non la liste des filtres : les nommer tous allongerait le nom
    // sans le rendre plus clair, et il en existe six.
    else if (filtresActifs) morceaux.push('filtre');
    morceaux.push(jour);
    return morceaux.join('_') + '.xlsx';
}

/**
 * Construit le classeur. SÉPARÉ de l'écriture du fichier pour qu'un banc puisse
 * le vérifier sans navigateur : « le bouton ne lève pas d'erreur » ne dit rien du
 * contenu, et c'est le contenu qui sort de l'application.
 */
export function construireClasseur(lignes) {
    const entetes = COLONNES_EXPORT.map((c) => c.entete);
    const corps = (lignes || []).map((l) => COLONNES_EXPORT.map((c) => c.valeur(l)));

    const feuille = XLSX.utils.aoa_to_sheet([entetes, ...corps]);

    // Largeurs lisibles : un classeur dont toutes les colonnes font 8 caractères
    // s'ouvre sur des `####` et donne l'impression d'un export raté.
    feuille['!cols'] = [
        { wch: 22 }, { wch: 16 }, { wch: 13 }, { wch: 12 }, { wch: 11 },
        { wch: 9 }, { wch: 12 }, { wch: 7 },
        { wch: 11 }, { wch: 10 }, { wch: 9 }, { wch: 14 }, { wch: 13 }
    ];
    // La ligne d'en-tête reste visible quand on descend dans 147 lignes.
    feuille['!freeze'] = { xSplit: 0, ySplit: 1 };

    const classeur = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(classeur, feuille, 'Étudiants');
    return classeur;
}

/**
 * Le geste complet : construire, puis écrire. Rend le nom du fichier écrit, pour
 * que l'écran puisse le DIRE — un téléchargement silencieux passe pour un clic
 * sans effet, surtout quand le navigateur range le fichier sans rien afficher.
 */
export function exporterListe(lignes, contexte) {
    const nom = nomFichierExport(contexte || {});
    XLSX.writeFile(construireClasseur(lignes), nom);
    return nom;
}
